'use client';

import * as React from 'react';
import { usePathname } from 'next/navigation';
import { Search } from 'lucide-react';
import type { CityCode } from '@loci/city-editions';

import { DialogClose } from '@/components/atoms/primitives/dialog';
import { cn } from '@/components/atoms/primitives/utils';
import { GN_ACTIVE_LINK_GLOW_STATIC } from '@/components/molecules/CityCoordStrip/GlobalNavCoordStrip';
import {
  FOCUS_RING,
  NAV_HUB_LINKS,
  SECONDARY_NAV_ITEMS,
  resolveNavItemHref,
  SIGN_UP_LINK,
  cityNavSignUpHref,
  isCrossOrigin,
  type CityHeaderActiveNav,
} from '@/components/molecules/CityHeader/cityHeaderShared';
import { SurfaceLink } from '@/components/atoms/SurfaceLink';
import {
  activeCategorySlug,
  buildCategoryNavEntries,
} from '@/components/molecules/CategoriesDropdown/categoryNavEntries';
import {
  useCategoryTonightCounts,
  type CategoryTonightCounts,
} from '@/hooks/useCategoryTonightCounts';
import { useCitySignInHref } from '@/hooks/useCitySignInHref';
import { useDiscoverHref } from '@/hooks/useDiscoverHref';
import { useCitySignUpHref } from '@/hooks/useCitySignUpHref';
import { useSearchSheet } from '@/hooks/useSearchSheet';
import { useCityIsStaging } from '@/lib/city-context.client';
import type { CityAuthUser } from '@/lib/city-nav/cityAuthUser';
import { cityHostLabel } from '@/lib/cross-host-urls';
import { formatSearchCityLabel } from '@/lib/copy/searchCityCopy';

import { MenuWayfinderCard } from './MenuWayfinderCard';

/**
 * CityMenuSheetContent — surface-aware body for the shared `NavMenuSheet`
 * shell on the marketing-city surface (WEB-614 / DES-59 / DES-60 / WEB-639 /
 * WEB-640 / FRE-1389).
 *
 * Fills the empty shell from WEB-613 with the v2 sitemap: three primary hubs,
 * the ten categories as a labelled chip group (WEB-1180 / DES-108 —
 * Categories is a disclosure on the bar, never a hub link), secondary Search
 * Charlotte only, a carded Wayfinder entry that opens the
 * header sheet, and — for anon visitors — a bottom-pinned auth block.
 *
 * Auth block: anon keeps the amber Sign up CTA + Sign in link (DES-59 B /
 * F-04). Signed in, the block is gone: identity, Notifications and Sign out
 * live in the avatar's account drawer (`CityAccountSheetContent`, DS 1.9.1).
 * The sitemap itself is for every visitor — hubs never change with auth.
 *
 * WEB-639 deliberate omissions (do not re-add without the pages launching):
 * Venues/Artists secondary rows, footer meta line, inline Wayfinder layers,
 * EXPLORE / WAYFINDER section eyebrows.
 */

// WEB-1239 / WEB-1273: hub rows carry no caption. A caption must be true for
// the page it opens, and the menu has no cheap path to those pages' counts:
// the Neighborhoods and Categories counts were wrong, and /discover is not
// limited to tonight.
// Do not re-add one without the destination's own query behind it.

const HUB_ROW = cn(
  'flex min-h-14 items-center justify-between gap-3 px-3.5 py-3 no-underline md:px-5',
  'font-display text-[27px] font-semibold leading-tight tracking-[-0.02em] text-fg-1',
  'transition-colors duration-150 hover:bg-white/[0.04] motion-reduce:transition-none',
  FOCUS_RING,
);

const SECONDARY_ROW = cn(
  'flex min-h-11 items-center gap-3 rounded-ds-md px-3 py-2.5 no-underline',
  'font-display text-[14px] font-medium text-fg-1',
  'transition-colors duration-150 hover:bg-white/[0.06] motion-reduce:transition-none',
  FOCUS_RING,
);

export interface CityMenuSheetContentProps {
  readonly cityCode: CityCode;
  readonly cityName: string;
  /** Which primary hub is the current page (or `null`). */
  readonly activeNav?: CityHeaderActiveNav;
  /**
   * Signup destination; `from=city-nav` is appended. Defaults to cross-host
   * signup with `next=<this page>` (FRE-1389).
   */
  readonly signUpUrl?: string;
  /**
   * Sign-in destination. Defaults to `crossHostLogin(currentUrl, { force: true })`
   * — `force` so stale cookies cannot bounce returning users off the login form
   * (FRE-784), `currentUrl` so a successful sign-in returns them to this page
   * instead of the app feed (WEB-898).
   */
  readonly signInUrl?: string;
  /**
   * Signed-in identity (WEB-640). When set, the anon auth block is omitted —
   * identity lives in the account drawer (FRE-1389). Omit / `null` keeps the
   * anon block byte-stable.
   */
  readonly authUser?: CityAuthUser | null;
  /**
   * Cookie/hint-armed `/auth/me` still settling (WEB-640). When true and
   * `authUser` is null, the anon block is marked `aria-busy` with a
   * "Loading account" status until the probe resolves.
   */
  readonly isAuthPending?: boolean;
  /**
   * Called synchronously before Search Charlotte opens the shared SearchSheet,
   * so the host can suppress menu focus-return for that gesture (WEB-614).
   */
  readonly onBeforeOpenSearch?: () => void;
  /**
   * Called synchronously before the Wayfinder card opens the header sheet,
   * so the host can suppress menu focus-return (WEB-639).
   */
  readonly onBeforeOpenWayfinder?: () => void;
  /** Pathname override — Storybook / tests. Defaults to `usePathname()`. */
  readonly pathname?: string | null;
  /**
   * Tonight counts seed for the Categories chips (WEB-1180). Omit (default)
   * to fetch on mount — the body only mounts while the takeover is open.
   */
  readonly categoryTonightCounts?: CategoryTonightCounts | null;
  readonly className?: string;
}

export function CityMenuSheetContent({
  cityCode,
  cityName,
  activeNav = null,
  signUpUrl,
  signInUrl,
  authUser = null,
  isAuthPending = false,
  onBeforeOpenSearch,
  onBeforeOpenWayfinder,
  pathname: pathnameOverride,
  categoryTonightCounts,
  className,
}: CityMenuSheetContentProps): React.ReactElement {
  const routerPathname = usePathname();
  const pathname = pathnameOverride !== undefined ? pathnameOverride : routerPathname;
  // The sheet body only mounts while the takeover is open, so this is the
  // visitor's intent — fetch tonight's counts now (memoised per city).
  const fetchedCounts = useCategoryTonightCounts(cityCode, categoryTonightCounts === undefined);
  const tonightCounts = categoryTonightCounts !== undefined ? categoryTonightCounts : fetchedCounts;
  const categoryEntries = React.useMemo(
    () => buildCategoryNavEntries(tonightCounts),
    [tonightCounts],
  );
  const currentCategorySlug = activeCategorySlug(pathname);
  const { setOpen: setSearchOpen } = useSearchSheet();
  const isStaging = useCityIsStaging();
  const host = cityHostLabel(cityCode, { isStaging });
  const signUpHref = cityNavSignUpHref(useCitySignUpHref(cityCode, signUpUrl, { isStaging }));
  const signInHref = useCitySignInHref(cityCode, signInUrl, { isStaging });
  // WEB-1231 / R25: Discover-bound hubs keep a signed-in viewer on `app.*`.
  const toDiscover = useDiscoverHref(cityCode);
  // Auth-block state is driven by `authUser` / `isAuthPending` (WEB-640).
  const hasAuthUser = authUser !== null;
  const authState = isAuthPending ? 'pending' : 'anon';

  const handleSearchClick = React.useCallback(() => {
    onBeforeOpenSearch?.();
    setSearchOpen(true, { trigger: 'button' });
  }, [onBeforeOpenSearch, setSearchOpen]);

  return (
    <div
      data-testid="city-menu-sheet-content"
      className={cn('flex min-h-0 flex-1 flex-col', className)}
    >
      <div className="flex-1 px-0 pb-4">
        <nav aria-label="Primary hubs">
          <ul className="flex flex-col">
            {NAV_HUB_LINKS.map((item, index) => {
              const isActive = item.id === activeNav;
              const isLast = index === NAV_HUB_LINKS.length - 1;
              return (
                <li
                  key={item.id}
                  className={cn('border-b border-white/[0.07]', isLast && 'border-b-0')}
                >
                  <DialogClose asChild>
                    <SurfaceLink
                      href={resolveNavItemHref(item, toDiscover)}
                      aria-current={isActive ? 'page' : undefined}
                      data-active={isActive ? 'true' : undefined}
                      data-testid={`city-menu-hub-${item.id}`}
                      className={cn(HUB_ROW, isActive && GN_ACTIVE_LINK_GLOW_STATIC)}
                    >
                      <span className="truncate">{item.label}</span>
                    </SurfaceLink>
                  </DialogClose>
                </li>
              );
            })}
          </ul>
        </nav>

        {/*
          WEB-1180 / DES-108: Categories is a labelled group of the ten hub
          links, not a hub link. A known zero tonight is dashed + dimmed,
          never hidden; there is no "All categories" chip.
        */}
        <nav
          aria-labelledby="city-menu-categories-label"
          className="city-menu-cats"
          data-testid="city-menu-categories"
        >
          <span id="city-menu-categories-label" className="city-menu-cats__label">
            Categories
          </span>
          <ul className="city-menu-cats__list">
            {categoryEntries.map((entry) => (
              <li key={entry.slug}>
                <DialogClose asChild>
                  <SurfaceLink
                    href={entry.href}
                    aria-label={entry.ariaLabel}
                    aria-current={currentCategorySlug === entry.slug ? 'page' : undefined}
                    data-dim={entry.dim ? '1' : undefined}
                    data-testid={`city-menu-category-${entry.slug}`}
                    className={cn('city-menu-cats__chip a-hit-floor', FOCUS_RING)}
                  >
                    {entry.label}
                  </SurfaceLink>
                </DialogClose>
              </li>
            ))}
          </ul>
        </nav>

        {/*
          WEB-639: Venues + Artists stay out of secondary nav until those pages
          launch. Re-add rows only then — do not feature-flag or comment them in.
        */}
        <nav aria-label="Secondary navigation" className="mt-2 px-2 md:px-3">
          <ul className="flex flex-col gap-0.5" data-testid="city-menu-secondary-nav">
            {SECONDARY_NAV_ITEMS.map((item) => {
              // WEB-620 / PL-09 — same stem as SearchSheet + CityHeaderSearch.
              const searchLabel = formatSearchCityLabel(cityName);
              return (
                <li key={item.id}>
                  <DialogClose asChild>
                    <button
                      type="button"
                      onClick={handleSearchClick}
                      aria-label={searchLabel}
                      data-testid="city-menu-secondary-search"
                      className={cn(SECONDARY_ROW, 'w-full text-left')}
                    >
                      <Search className="size-4 shrink-0 text-fg-2" aria-hidden="true" />
                      <span className="truncate">{searchLabel}</span>
                    </button>
                  </DialogClose>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="mt-3">
          <MenuWayfinderCard
            hostLabel={host}
            subtitle="Public discovery · switch layer or city"
            ariaLabel={`You are here · ${host}. Open Wayfinder to switch layer or city.`}
            testId="city-menu-wayfinder"
            onBeforeOpen={onBeforeOpenWayfinder}
          />
        </div>
      </div>

      {/* Auth block — pinned, anon only. DES-59 (B) amber Sign up + visible
          Sign in (F-04). Signed in there is nothing to convert, and identity /
          Sign out live in the account drawer (FRE-1389). */}
      {hasAuthUser ? null : (
        <div
          data-testid="city-menu-auth-block"
          data-auth={authState}
          className="shrink-0 border-t border-white/[0.08] px-3.5 py-4 md:px-5"
        >
          <div className="flex flex-col items-stretch gap-3" aria-busy={isAuthPending || undefined}>
            {isAuthPending ? (
              <span data-testid="city-menu-auth-pending" className="sr-only">
                Loading account
              </span>
            ) : null}
            <DialogClose asChild>
              <a
                href={signUpHref}
                data-testid="city-menu-sign-up"
                // Keep the menu CTA at the 44px floor — SIGN_UP_LINK's
                // `lg:min-h-9` is for the header pill band, not this takeover.
                className={cn(SIGN_UP_LINK, FOCUS_RING, 'h-auto w-full min-h-11 lg:min-h-11')}
                {...(isCrossOrigin(signUpHref) ? { target: '_blank', rel: 'noreferrer' } : {})}
              >
                Sign up. Build your footprint
              </a>
            </DialogClose>
            <DialogClose asChild>
              <a
                href={signInHref}
                data-testid="city-menu-sign-in"
                className={cn(
                  'inline-flex min-h-11 items-center justify-center rounded-full px-3',
                  'font-display text-small font-medium text-fg-2',
                  'underline-offset-4 hover:text-fg-1 hover:underline',
                  FOCUS_RING,
                )}
                {...(isCrossOrigin(signInHref) ? { target: '_blank', rel: 'noreferrer' } : {})}
              >
                Have an account? Sign in
              </a>
            </DialogClose>
          </div>
        </div>
      )}
    </div>
  );
}
